import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

export default function DashboardPage({ isDarkMode }) {
  const [userName, setUserName] = useState("User");
  const [allTranscripts, setAllTranscripts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [aiGenerationCount, setAiGenerationCount] = useState(1);
  const [aiGenerationLimit] = useState(1);
  const [usagePercentage, setUsagePercentage] = useState(0);
  const [generateUrl, setGenerateUrl] = useState("");
  const [generateLoading, setGenerateLoading] = useState(false);
  const [generateError, setGenerateError] = useState(null);
  const [filterType, setFilterType] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [savingTranscriptId, setSavingTranscriptId] = useState(null);
  const [windowWidth, setWindowWidth] = useState(typeof window !== "undefined" ? window.innerWidth : 1024);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const hamburgerRef = useRef(null);
  const navigate = useNavigate();

  // ============ RESPONSIVE BREAKPOINTS ============
  const isMobile = windowWidth < 768;
  const isTablet = windowWidth >= 768 && windowWidth < 1024;
  const isDesktop = windowWidth >= 1024;

  // ============ RESPONSIVE HELPERS ============
  const getPadding = () => {
    if (isMobile) return "clamp(12px, 4vw, 16px)";
    if (isTablet) return "clamp(16px, 3vw, 20px)";
    return "20px";
  };

  const getGap = () => {
    if (isMobile) return "clamp(12px, 3vw, 16px)";
    if (isTablet) return "clamp(16px, 2vw, 20px)";
    return "28px";
  };

  const getHeadingSize = () => {
    if (isMobile) return "clamp(20px, 6vw, 28px)";
    if (isTablet) return "clamp(24px, 4vw, 32px)";
    return "32px";
  };

  const getSubHeadingSize = () => {
    if (isMobile) return "clamp(13px, 3.5vw, 15px)";
    if (isTablet) return "clamp(14px, 2.5vw, 16px)";
    return "16px";
  };

  const extractYouTubeId = (url) => {
    if (!url) return null;
    const regex = /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/.*(?:v=|vi=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
    const match = url.match(regex);
    return match ? match[1] : null;
  };

  // ============ WINDOW RESIZE LISTENER ============
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // ============ CLOSE MOBILE MENU WITH ESC ============
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && mobileMenuOpen) {
        setMobileMenuOpen(false);
      }
    };
    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [mobileMenuOpen]);

  // ============ CLOSE MENU ON CLICK OUTSIDE ============
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        mobileMenuOpen &&
        menuRef.current &&
        hamburgerRef.current &&
        !menuRef.current.contains(e.target) &&
        !hamburgerRef.current.contains(e.target)
      ) {
        setMobileMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [mobileMenuOpen]);

  // ============ CLOSE MENU WHEN LEAVING MOBILE VIEW ============
  useEffect(() => {
    if (!isMobile && mobileMenuOpen) {
      setMobileMenuOpen(false);
    }
  }, [isMobile, mobileMenuOpen]);

  const handleGenerateTranscript = async () => {
    if (!generateUrl.trim()) {
      alert("Please enter a YouTube URL");
      return;
    }

    const videoId = extractYouTubeId(generateUrl.trim());
    if (!videoId) {
      alert("Please enter a valid YouTube URL");
      return;
    }

    setGenerateLoading(true);
    setGenerateError(null);

    try {
      const transcriptRes = await axios.post("/api/transcript/", {
        url: generateUrl.trim(),
        target_lang: null,
      });

      if (!transcriptRes.data || !transcriptRes.data.success) {
        throw new Error(transcriptRes.data?.error || "Failed to fetch transcript");
      }

      const snippets = transcriptRes.data.snippets || [];
      const transcript_text = snippets.map(s => `[${s.timestamp}] ${s.text}`).join("\n");

      let duration_seconds = 0;
      snippets.forEach(s => {
        const end = (s.start || 0) + (s.duration || 0);
        if (end > duration_seconds) duration_seconds = end;
      });

      const word_count = transcript_text.split(/\s+/).filter(Boolean).length;

      let title = "";
      let channel_name = "";
      try {
        const oe = await axios.get("/api/youtube/oembed/", {
          params: { video_id: videoId },
          timeout: 20000,
        });
        if (oe.data && !oe.data.error) {
          title = oe.data.title || "";
          channel_name = oe.data.author_name || "";
        } else {
          title = `Video ${videoId}`;
        }
      } catch (e) {
        console.warn("oEmbed failed", e.message);
        title = `Video ${videoId}`;
      }

      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const createRes = await axios.post("/api/create_transcript/", {
        youtube_id: videoId,
        title: title || `Video ${videoId}`,
        channel_name,
        duration_seconds,
        word_count,
        language: transcriptRes.data.language_code || "en",
        transcript_text,
      }, { headers });

      if (createRes.data && createRes.data.success) {
        navigate(`/t/${videoId}`);
      } else {
        throw new Error(createRes.data?.error || "Failed to save transcript");
      }
    } catch (error) {
      console.error(error);
      setGenerateError(error.message || "Error generating transcript.");
      alert(error.message || "Error generating transcript. Please try again.");
    } finally {
      setGenerateLoading(false);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("token");
    const userEmail = localStorage.getItem("userEmail");

    if (!token) {
      navigate("/signin");
      return;
    }

    if (userEmail) {
      setUserName(userEmail.split("@")[0]);
    }

    fetchUserDashboard();
  }, [navigate]);

  const fetchUserDashboard = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get("/api/user-transcripts/", {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        const transcripts = response.data.transcripts || [];
        setAllTranscripts(transcripts);

        if (response.data.ai_remaining !== undefined) {
          setAiGenerationCount(response.data.ai_remaining);
          setUsagePercentage(
            ((aiGenerationLimit - response.data.ai_remaining) / aiGenerationLimit) * 100
          );
        }
      } else {
        setError("Failed to load dashboard");
      }
    } catch (err) {
      console.error("Error fetching dashboard:", err);
      setError("Error loading dashboard");
    } finally {
      setLoading(false);
    }
  };

  const handleTranscriptClick = (youtubeId) => {
    navigate(`/t/${youtubeId}`);
  };

  const toggleSaveTranscript = async (event, youtubeId) => {
    event.stopPropagation();
    const transcript = allTranscripts.find(t => t.youtube_id === youtubeId);
    if (!transcript) return;

    setSavingTranscriptId(youtubeId);
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      await axios.post(`/api/transcript-save/`, {
        youtube_id: youtubeId,
        is_saved: !transcript.is_saved
      }, { headers });

      setAllTranscripts(allTranscripts.map(t =>
        t.youtube_id === youtubeId
          ? { ...t, is_saved: !t.is_saved }
          : t
      ));
    } catch (err) {
      console.error("Error toggling save:", err);
    } finally {
      setSavingTranscriptId(null);
    }
  };

  const filteredTranscripts = allTranscripts
    .filter(t => filterType === "all" ? true : t.is_saved)
    .filter(t =>
      t.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      t.channel_name.toLowerCase().includes(searchQuery.toLowerCase())
    );

  const savedCount = allTranscripts.filter(t => t.is_saved).length;

  const handleUpgradePremium = () => {
    alert("Premium upgrade coming soon!");
  };

  if (loading) {
    return (
      <div style={{
        padding: getPadding(),
        textAlign: "center",
        backgroundColor: isDarkMode ? "#0d0d0d" : "#f7fafd",
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        color: isDarkMode ? "#fff" : "#000"
      }}>
        <div style={{
          width: "clamp(40px, 15vw, 60px)",
          height: "clamp(40px, 15vw, 60px)",
          border: "4px solid #e3e6e8",
          borderTop: "4px solid #2363eb",
          borderRadius: "50%",
          animation: "spin 1s linear infinite",
          marginBottom: "20px"
        }}></div>
        <div style={{ fontSize: "clamp(16px, 5vw, 24px)", fontWeight: "bold", marginBottom: "10px" }}>
          Loading Dashboard...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        padding: getPadding(),
        textAlign: "center",
        color: "red",
        backgroundColor: isDarkMode ? "#0d0d0d" : "#f7fafd",
        minHeight: "calc(100vh - 180px)",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center"
      }}>
        <div style={{ fontSize: "clamp(14px, 4vw, 18px)", marginBottom: "20px" }}>
          {error}
        </div>
        <button
          onClick={() => navigate("/")}
          style={{
            padding: "clamp(8px, 2vw, 10px) clamp(14px, 3vw, 20px)",
            backgroundColor: "#2363eb",
            color: "white",
            border: "none",
            borderRadius: "7px",
            cursor: "pointer",
            fontSize: "clamp(12px, 2.5vw, 14px)",
            fontWeight: "600",
          }}
        >
          ← Back to Home
        </button>
      </div>
    );
  }

  return (
    <>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          @keyframes slideDown {
            from {
              transform: translateY(-10px);
              opacity: 0;
            }
            to {
              transform: translateY(0);
              opacity: 1;
            }
          }
          * { box-sizing: border-box; }
          body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif; }
        `}
      </style>

      <div style={{
        backgroundColor: isDarkMode ? "#0d0d0d" : "#f7fafd",
        minHeight: "100vh",
        padding: getPadding(),
        fontFamily: "'Inter', sans-serif",
      }}>
        {/* Header */}
        <div style={{
          maxWidth: "1200px",
          margin: "0 auto clamp(16px, 4vw, 32px) auto",
          paddingBottom: "clamp(12px, 3vw, 20px)",
          borderBottom: `1px solid ${isDarkMode ? "#333" : "#e3e6e8"}`,
        }}>
          <div>
            <h1 style={{
              margin: "0 0 clamp(4px, 1.5vw, 8px) 0",
              fontSize: getHeadingSize(),
              fontWeight: "700",
              color: isDarkMode ? "#fff" : "#21242b"
            }}>
              Welcome back, {userName}
            </h1>
            <p style={{
              margin: "0",
              fontSize: "clamp(12px, 2.5vw, 14px)",
              color: "#636a7b"
            }}>
              Generate and manage your transcripts
            </p>
          </div>
        </div>

        {/* Main Grid - Responsive */}
        <div style={{
          maxWidth: "1200px",
          margin: "0 auto",
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : isTablet ? "1fr 260px" : "1fr 320px",
          gap: getGap(),
          alignItems: "start"
        }}>
          {/* Left Column */}
          <div>
            {/* Generate Transcript Section */}
            <div style={{
              backgroundColor: isDarkMode ? "#1a1a1a" : "#ffffff",
              border: `1.5px solid ${isDarkMode ? "#333" : "#f1f2f4"}`,
              borderRadius: "10px",
              padding: "clamp(16px, 4vw, 24px)",
              marginBottom: "clamp(16px, 4vw, 28px)"
            }}>
              <h2 style={{
                margin: "0 0 clamp(10px, 2.5vw, 16px) 0",
                fontSize: getSubHeadingSize(),
                fontWeight: "700",
                color: isDarkMode ? "#fff" : "#21242b"
              }}>
                ✍️ Generate Transcript
              </h2>
              <div style={{
                display: "flex",
                gap: "clamp(8px, 2vw, 12px)",
                flexWrap: isMobile ? "wrap" : "nowrap",
              }}>
                <input
                  type="text"
                  value={generateUrl}
                  onChange={(e) => setGenerateUrl(e.target.value)}
                  placeholder="Paste a YouTube link..."
                  style={{
                    flex: 1,
                    minWidth: isMobile ? "100%" : "auto",
                    padding: "clamp(8px, 2vw, 12px) clamp(10px, 2.5vw, 16px)",
                    backgroundColor: isDarkMode ? "#0d0d0d" : "#f8f9fa",
                    border: `1.5px solid ${isDarkMode ? "#333" : "#e3e6e8"}`,
                    borderRadius: "7px",
                    fontSize: "clamp(12px, 2.5vw, 14px)",
                    color: isDarkMode ? "#fff" : "#21242b",
                    outline: "none",
                    transition: "border-color 0.3s",
                    minHeight: "clamp(36px, 9vw, 44px)",
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = "#2363eb";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = isDarkMode ? "#333" : "#e3e6e8";
                  }}
                />
                <button
                  onClick={handleGenerateTranscript}
                  disabled={generateLoading}
                  style={{
                    padding: "clamp(8px, 2vw, 12px) clamp(12px, 3vw, 24px)",
                    backgroundColor: generateLoading ? "#66a1ff" : "rgb(0, 123, 255)",
                    color: "white",
                    border: "none",
                    borderRadius: "7px",
                    cursor: generateLoading ? "not-allowed" : "pointer",
                    fontSize: "clamp(11px, 2.5vw, 14px)",
                    fontWeight: "600",
                    transition: "background-color 0.3s",
                    whiteSpace: "nowrap",
                    minHeight: "clamp(36px, 9vw, 44px)",
                    display: "flex",
                    alignItems: "center",
                    gap: "clamp(4px, 1.5vw, 6px)",
                    justifyContent: "center",
                  }}
                >
                  {generateLoading ? (
                    <>
                      <span style={{ fontSize: "clamp(10px, 2.5vw, 12px)" }}>⏳</span>
                      <span style={{ display: isMobile ? "none" : "inline" }}>Generating...</span>
                    </>
                  ) : (
                    <>
                      <span style={{ display: isMobile ? "none" : "inline" }}>Generate</span>
                      <span style={{ fontSize: "clamp(12px, 2.5vw, 16px)", fontWeight: "900" }}>→</span>
                    </>
                  )}
                </button>
              </div>
              {generateError && (
                <p style={{
                  marginTop: "clamp(6px, 1.5vw, 10px)",
                  fontSize: "clamp(11px, 2vw, 13px)",
                  color: "#e74c3c"
                }}>
                  {generateError}
                </p>
              )}
            </div>

            {/* My Transcripts - Combined List */}
            <div style={{
              backgroundColor: isDarkMode ? "#1a1a1a" : "#ffffff",
              border: `1.5px solid ${isDarkMode ? "#333" : "#f1f2f4"}`,
              borderRadius: "10px",
              overflow: "hidden"
            }}>
              <div style={{
                padding: "clamp(14px, 3vw, 20px) clamp(14px, 3vw, 24px)",
                borderBottom: `1px solid ${isDarkMode ? "#333" : "#f1f2f4"}`,
              }}>
                <div style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: isMobile ? "flex-start" : "center",
                  marginBottom: "clamp(10px, 2.5vw, 16px)",
                  flexWrap: isMobile ? "wrap" : "nowrap",
                  gap: "clamp(8px, 2vw, 12px)",
                }}>
                  <div style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "clamp(6px, 1.5vw, 12px)"
                  }}>
                    <h3 style={{
                      margin: "0",
                      fontSize: "clamp(14px, 2.5vw, 16px)",
                      fontWeight: "700",
                      color: isDarkMode ? "#fff" : "#21242b"
                    }}>
                      📚 My Transcripts
                    </h3>
                    <span style={{
                      backgroundColor: "#f0f6ff",
                      color: "#2363eb",
                      padding: "clamp(2px, 1vw, 4px) clamp(8px, 2vw, 12px)",
                      borderRadius: "20px",
                      fontSize: "clamp(10px, 2vw, 12px)",
                      fontWeight: "600",
                      whiteSpace: "nowrap",
                    }}>
                      {allTranscripts.length}
                    </span>
                  </div>
                  <div style={{
                    display: "flex",
                    gap: "clamp(4px, 1.5vw, 8px)",
                    flexWrap: "wrap",
                    width: isMobile ? "100%" : "auto",
                  }}>
                    <button
                      onClick={() => setFilterType("all")}
                      style={{
                        padding: "clamp(4px, 1.5vw, 6px) clamp(8px, 2vw, 12px)",
                        backgroundColor: filterType === "all" ? "rgb(0, 123, 255)" : isDarkMode ? "#0d0d0d" : "#f8f9fa",
                        color: filterType === "all" ? "white" : isDarkMode ? "#fff" : "#21242b",
                        border: filterType === "all" ? "none" : `1px solid ${isDarkMode ? "#333" : "#e3e6e8"}`,
                        borderRadius: "5px",
                        cursor: "pointer",
                        fontSize: "clamp(11px, 2vw, 12px)",
                        fontWeight: "600",
                        transition: "all 0.2s",
                        minHeight: "clamp(28px, 7vw, 32px)",
                        flex: isMobile ? "1 1 auto" : "0 0 auto",
                      }}
                    >
                      All
                    </button>
                    <button
                      onClick={() => setFilterType("saved")}
                      style={{
                        padding: "clamp(4px, 1.5vw, 6px) clamp(8px, 2vw, 12px)",
                        backgroundColor: filterType === "saved" ? "#f39c12" : isDarkMode ? "#0d0d0d" : "#f8f9fa",
                        color: filterType === "saved" ? "white" : isDarkMode ? "#fff" : "#21242b",
                        border: filterType === "saved" ? "none" : `1px solid ${isDarkMode ? "#333" : "#e3e6e8"}`,
                        borderRadius: "5px",
                        cursor: "pointer",
                        fontSize: "clamp(11px, 2vw, 12px)",
                        fontWeight: "600",
                        transition: "all 0.2s",
                        minHeight: "clamp(28px, 7vw, 32px)",
                        flex: isMobile ? "1 1 auto" : "0 0 auto",
                        whiteSpace: "nowrap",
                      }}
                    >
                      ⭐ Saved ({savedCount})
                    </button>
                  </div>
                </div>

                {/* Search Input */}
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search transcripts..."
                  style={{
                    width: "100%",
                    padding: "clamp(6px, 1.5vw, 8px) clamp(8px, 2vw, 12px)",
                    backgroundColor: isDarkMode ? "#0d0d0d" : "#f8f9fa",
                    border: `1.5px solid ${isDarkMode ? "#333" : "#e3e6e8"}`,
                    borderRadius: "5px",
                    fontSize: "clamp(12px, 2vw, 13px)",
                    color: isDarkMode ? "#fff" : "#21242b",
                    outline: "none",
                    transition: "border-color 0.3s",
                    minHeight: "clamp(32px, 8vw, 40px)",
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = "#2363eb";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = isDarkMode ? "#333" : "#e3e6e8";
                  }}
                />
              </div>

              {/* Transcripts List */}
              {filteredTranscripts.length > 0 ? (
                <div style={{
                  maxHeight: isMobile ? "400px" : "600px",
                  overflowY: "auto",
                }}>
                  {filteredTranscripts.map((transcript, idx) => (
                    <div
                      key={transcript.youtube_id}
                      onClick={() => handleTranscriptClick(transcript.youtube_id)}
                      style={{
                        padding: "clamp(10px, 2.5vw, 16px) clamp(12px, 3vw, 24px)",
                        borderBottom: idx < filteredTranscripts.length - 1 ? `1px solid ${isDarkMode ? "#333" : "#f1f2f4"}` : "none",
                        cursor: "pointer",
                        display: "flex",
                        gap: "clamp(8px, 2vw, 16px)",
                        alignItems: isMobile ? "flex-start" : "center",
                        transition: "background-color 0.2s",
                        backgroundColor: isDarkMode ? "#1a1a1a" : "#ffffff",
                        flexWrap: isMobile ? "wrap" : "nowrap",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = isDarkMode ? "#242424" : "#f8f9fa";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = isDarkMode ? "#1a1a1a" : "#ffffff";
                      }}
                    >
                      {transcript.thumbnail_url && (
                        <img
                          src={transcript.thumbnail_url}
                          alt={transcript.title}
                          style={{
                            width: isMobile ? "clamp(60px, 20vw, 70px)" : "80px",
                            height: isMobile ? "clamp(35px, 12vw, 42px)" : "45px",
                            borderRadius: "6px",
                            objectFit: "cover",
                            flexShrink: 0
                          }}
                        />
                      )}
                      <div style={{
                        flex: 1,
                        minWidth: 0,
                        order: isMobile ? 3 : 0,
                        flexBasis: isMobile ? "100%" : "auto",
                        marginTop: isMobile ? "clamp(6px, 1.5vw, 8px)" : 0,
                      }}>
                        <h4 style={{
                          margin: "0 0 clamp(3px, 1vw, 6px) 0",
                          fontSize: "clamp(12px, 2.5vw, 14px)",
                          fontWeight: "600",
                          color: isDarkMode ? "#fff" : "#21242b",
                          lineHeight: "1.4",
                          wordBreak: "break-word",
                        }}>
                          {transcript.title}
                        </h4>
                        <p style={{
                          margin: "0",
                          fontSize: "clamp(10px, 2vw, 12px)",
                          color: "#636a7b"
                        }}>
                          {transcript.channel_name || "Unknown"} • {new Date(transcript.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div style={{
                        display: "flex",
                        gap: "clamp(6px, 1.5vw, 12px)",
                        alignItems: "center",
                        flexShrink: 0,
                        order: isMobile ? 2 : 0,
                      }}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleTranscriptClick(transcript.youtube_id);
                          }}
                          style={{
                            padding: "clamp(6px, 1.5vw, 8px) clamp(6px, 1.5vw, 8px)",
                            backgroundColor: "#f0f6ff",
                            color: "#2363eb",
                            border: "none",
                            borderRadius: "5px",
                            cursor: "pointer",
                            fontSize: "clamp(10px, 2vw, 12px)",
                            fontWeight: "600",
                            whiteSpace: "nowrap",
                            display: "flex",
                            alignItems: "center",
                            gap: "3px",
                            transition: "all 0.2s",
                            minHeight: "clamp(28px, 7vw, 32px)",
                            minWidth: "clamp(28px, 7vw, 32px)",
                            justifyContent: "center",
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.backgroundColor = "#e8f0ff";
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor = "#f0f6ff";
                          }}
                          title="Open transcript"
                        >
                          <span style={{ fontSize: "clamp(12px, 2.5vw, 14px)" }}>📖</span>
                          <span style={{ display: isMobile ? "none" : "inline" }}>Open</span>
                        </button>
                        <button
                          onClick={(e) => toggleSaveTranscript(e, transcript.youtube_id)}
                          disabled={savingTranscriptId === transcript.youtube_id}
                          style={{
                            padding: "clamp(4px, 1vw, 6px) clamp(6px, 1.5vw, 12px)",
                            backgroundColor: "transparent",
                            color: transcript.is_saved ? "#f39c12" : "#999",
                            border: `1.5px solid ${transcript.is_saved ? "#f39c12" : "#ddd"}`,
                            borderRadius: "5px",
                            cursor: savingTranscriptId === transcript.youtube_id ? "not-allowed" : "pointer",
                            fontSize: "clamp(12px, 2.5vw, 16px)",
                            fontWeight: "600",
                            whiteSpace: "nowrap",
                            opacity: savingTranscriptId === transcript.youtube_id ? 0.6 : 1,
                            transition: "all 0.2s",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            minHeight: "clamp(28px, 7vw, 32px)",
                            minWidth: "clamp(28px, 7vw, 32px)",
                          }}
                          onMouseEnter={(e) => {
                            if (!savingTranscriptId) {
                              e.target.style.color = "#f39c12";
                              e.target.style.borderColor = "#f39c12";
                              e.target.style.backgroundColor = "rgba(243, 156, 18, 0.1)";
                            }
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.color = transcript.is_saved ? "#f39c12" : "#999";
                            e.target.style.borderColor = transcript.is_saved ? "#f39c12" : "#ddd";
                            e.target.style.backgroundColor = "transparent";
                          }}
                          title={transcript.is_saved ? "Remove from saved" : "Add to saved"}
                        >
                          {savingTranscriptId === transcript.youtube_id ? "…" : "★"}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{
                  padding: "clamp(24px, 6vw, 40px) clamp(14px, 3vw, 24px)",
                  textAlign: "center",
                  color: "#636a7b",
                  fontSize: "clamp(12px, 2.5vw, 14px)"
                }}>
                  {allTranscripts.length === 0
                    ? "📝 No transcripts yet. Create your first one!"
                    : "🔍 No transcripts match your search."}
                </div>
              )}
            </div>
          </div>

          {/* Right Sidebar - Hidden on Mobile, Visible on Tablet/Desktop */}
          {!isMobile && (
            <div style={{
              display: "flex",
              flexDirection: "column",
              gap: "clamp(10px, 2.5vw, 16px)"
            }}>
              {/* Usage Section */}
              <div style={{
                backgroundColor: isDarkMode ? "#1a1a1a" : "#ffffff",
                border: `1.5px solid ${isDarkMode ? "#333" : "#f1f2f4"}`,
                borderRadius: "10px",
                padding: "clamp(14px, 3vw, 20px)",
              }}>
                <h3 style={{
                  margin: "0 0 clamp(10px, 2.5vw, 16px) 0",
                  fontSize: "clamp(13px, 2vw, 15px)",
                  fontWeight: "700",
                  color: isDarkMode ? "#fff" : "#21242b"
                }}>
                  💠 AI Usage
                </h3>

                <div style={{
                  backgroundColor: isDarkMode ? "#0d0d0d" : "#f8f9fa",
                  borderRadius: "8px",
                  padding: "clamp(12px, 2vw, 16px)",
                  marginBottom: "clamp(10px, 2.5vw, 16px)"
                }}>
                  <div style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "clamp(6px, 1.5vw, 8px)"
                  }}>
                    <span style={{ fontSize: "clamp(11px, 1.8vw, 13px)", color: "#636a7b", fontWeight: "600" }}>
                      Free Plan
                    </span>
                    <span style={{ fontSize: "clamp(11px, 1.8vw, 13px)", fontWeight: "600", color: "#2363eb" }}>
                      {aiGenerationCount}/{aiGenerationLimit}
                    </span>
                  </div>
                  <div style={{
                    fontSize: "clamp(10px, 1.5vw, 12px)",
                    color: "#636a7b",
                    marginBottom: "clamp(6px, 1.5vw, 8px)"
                  }}>
                    AI Transcript Generations Used This Month
                  </div>
                  <div style={{
                    width: "100%",
                    height: "6px",
                    backgroundColor: isDarkMode ? "#333" : "#e3e6e8",
                    borderRadius: "3px",
                    overflow: "hidden"
                  }}>
                    <div style={{
                      width: `${usagePercentage}%`,
                      height: "100%",
                      backgroundColor: usagePercentage > 80 ? "#e74c3c" : "#19c37d",
                      transition: "width 0.3s ease"
                    }} />
                  </div>
                </div>

                <div style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "clamp(8px, 2vw, 12px)",
                  backgroundColor: isDarkMode ? "#0d0d0d" : "#f8f9fa",
                  borderRadius: "8px",
                  padding: "clamp(10px, 2vw, 12px)",
                  marginBottom: "clamp(8px, 2vw, 12px)",
                  borderLeft: "3px solid #2363eb"
                }}>
                  <div style={{
                    display: "flex",
                    gap: "clamp(8px, 2vw, 12px)",
                    alignItems: "flex-start"
                  }}>
                    <span style={{ fontSize: "clamp(14px, 3vw, 20px)" }}>✨</span>
                    <div>
                      <div style={{
                        fontSize: "clamp(11px, 1.8vw, 13px)",
                        fontWeight: "700",
                        color: isDarkMode ? "#fff" : "#21242b"
                      }}>
                        {aiGenerationLimit - aiGenerationCount} / {aiGenerationLimit} Available
                      </div>
                      <div style={{
                        fontSize: "clamp(10px, 1.5vw, 12px)",
                        color: "#636a7b",
                        marginTop: "2px"
                      }}>
                        {aiGenerationCount >= aiGenerationLimit
                          ? "Monthly limit reached"
                          : `${aiGenerationLimit - aiGenerationCount} generation${aiGenerationLimit - aiGenerationCount !== 1 ? 's' : ''} remaining`}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Quick Actions */}
              <div style={{
                backgroundColor: isDarkMode ? "#1a1a1a" : "#ffffff",
                border: `1.5px solid ${isDarkMode ? "#333" : "#f1f2f4"}`,
                borderRadius: "10px",
                padding: "clamp(14px, 3vw, 20px)"
              }}>
                <h3 style={{
                  margin: "0 0 clamp(8px, 2vw, 12px) 0",
                  fontSize: "clamp(13px, 2vw, 15px)",
                  fontWeight: "700",
                  color: isDarkMode ? "#fff" : "#21242b"
                }}>
                  Quick Actions
                </h3>
                <button
                  onClick={() => navigate("/")}
                  style={{
                    width: "100%",
                    padding: "clamp(8px, 2vw, 10px) clamp(10px, 2vw, 14px)",
                    backgroundColor: "transparent",
                    color: "#2363eb",
                    border: "1.5px solid #2363eb",
                    borderRadius: "7px",
                    cursor: "pointer",
                    fontSize: "clamp(11px, 2vw, 13px)",
                    fontWeight: "600",
                    marginBottom: "clamp(6px, 1.5vw, 8px)",
                    transition: "all 0.2s",
                    minHeight: "clamp(32px, 8vw, 40px)",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = "#f0f6ff";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = "transparent";
                  }}
                >
                  📖 Go to Homepage
                </button>
                <button
                  onClick={handleUpgradePremium}
                  style={{
                    width: "100%",
                    padding: "clamp(8px, 2vw, 10px) clamp(10px, 2vw, 14px)",
                    backgroundColor: "#f39c12",
                    color: "white",
                    border: "none",
                    borderRadius: "7px",
                    cursor: "pointer",
                    fontSize: "clamp(11px, 2vw, 13px)",
                    fontWeight: "600",
                    transition: "all 0.2s",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "clamp(4px, 1.5vw, 8px)",
                    minHeight: "clamp(32px, 8vw, 40px)",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = "#e67e22";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = "#f39c12";
                  }}
                >
                  🚀 <span>Upgrade</span>
                </button>
              </div>
            </div>
          )}

          {/* Mobile Bottom Actions */}
          {isMobile && (
            <div style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "clamp(8px, 2vw, 12px)",
              marginTop: "clamp(12px, 3vw, 16px)",
            }}>
              <button
                onClick={() => navigate("/")}
                style={{
                  padding: "clamp(10px, 2.5vw, 12px)",
                  backgroundColor: "transparent",
                  color: "#2363eb",
                  border: "1.5px solid #2363eb",
                  borderRadius: "7px",
                  cursor: "pointer",
                  fontSize: "clamp(11px, 2vw, 13px)",
                  fontWeight: "600",
                  transition: "all 0.2s",
                  minHeight: "clamp(36px, 9vw, 44px)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "4px",
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = "#f0f6ff";
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = "transparent";
                }}
              >
                <span style={{ fontSize: "clamp(12px, 2.5vw, 14px)" }}>📖</span>
                <span>Home</span>
              </button>
              <button
                onClick={handleUpgradePremium}
                style={{
                  padding: "clamp(10px, 2.5vw, 12px)",
                  backgroundColor: "#f39c12",
                  color: "white",
                  border: "none",
                  borderRadius: "7px",
                  cursor: "pointer",
                  fontSize: "clamp(11px, 2vw, 13px)",
                  fontWeight: "600",
                  transition: "all 0.2s",
                  minHeight: "clamp(36px, 9vw, 44px)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "4px",
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = "#e67e22";
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = "#f39c12";
                }}
              >
                <span style={{ fontSize: "clamp(12px, 2.5vw, 14px)" }}>🚀</span>
                <span>Upgrade</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
